import { FC, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import {
  Button,
  Form,
  FormField,
  Heading,
  Input,
  Text,
} from '@livechat/design-system-react-components';

import Config from 'Config';
import { SigninFormData, FormStates } from 'types/form';
import { CustomLink } from 'components/link/Link';
import Amplitude from 'services/Amplitude';
import Location from 'services/Location';
import { validateEmail } from 'helpers/validators/email';
import { submitPost } from 'helpers/form';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { saveItem } from 'helpers/storage/local-storage';
import { saveItem as saveSessionSotrageItem } from 'helpers/storage/session-storage';
import { detectProduct, getAppLinkByName, getProductName } from 'helpers/product';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';

import { LocalStorageKey } from 'constants/local-storage';
import { SignInRequestAccessRedirectForm } from './SignInRequestAccessRedirectForm';
import { SigninException } from './SigninException';
import Tracking from './signin-tracking';
import { SignInLoginMethods } from './SignInLoginMethods';

import * as styles from './styles';

export enum SigninMethod {
  Password = 'password',
  Google = 'Google',
  Microsoft = 'Microsoft',
  Apple = 'Apple',
  Saml = 'custom SSO',
}

type Props = {
  formData: SigninFormData;
  onDataChange: (data: SigninFormData) => void;
  isLegacy: boolean;
  isRequestAccessWrongAccountRedirect?: boolean;
};
export const SignInForm: FC<Props> = ({
  formData,
  onDataChange,
  isLegacy,
  isRequestAccessWrongAccountRedirect,
}) => {
  const { email, password } = formData;
  const [emailValidationError, setEmailValidationError] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');
  const [signinMethod, setSigninMethod] = useState<SigninMethod | null>(null);
  const [formState, setFormState] = useState(FormStates.Default);

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.persist();
    setSigninMethod(SigninMethod.Password);

    if (emailValidationError || passwordValidationError) {
      return;
    }

    try {
      setFormState(FormStates.Requesting);

      const product = detectProduct();
      if (product) {
        pushExternalRedirectToDataLayer(getAppLinkByName(getProductName(product)));
      }

      submitPost(`${Config.accounts_api_url}/?${buildOauthQuery(Config)}`, {
        email,
        password,
      });
    } catch (error: any) {
      throw new Error(`Couldn't sign in, ${error.message}`);
    }
  };

  const handleEmailInputChange = (e) => {
    onDataChange({ email: e.target.value, password });
    if (!validateEmail(e.target.value)) {
      setEmailValidationError('');
    }
  };

  const handlePasswordInputChange = (e) => {
    onDataChange({ password: e.target.value, email });
    if (e.target.value) {
      setPasswordValidationError('');
    }
  };

  useEffect(() => {
    if (Location.getReferrerHostname() === 'text.com') {
      Cookies.remove('product');
    }
    if (!isLegacy) {
      Amplitude.logEvent(email, 'Accounts: Open new login flow', { place: 'Login' });
    }
  }, [isLegacy, email]);

  useEffect(() => {
    if (signinMethod) {
      saveItem(LocalStorageKey.SigninMethod, signinMethod);
    }
  }, [signinMethod]);

  const handleSignInClick = () => {
    saveSessionSotrageItem('email', email);
    const emailValidation = validateEmail(email);

    if (emailValidation) {
      setEmailValidationError(emailValidation);
    }

    if (!password) {
      setPasswordValidationError('This field is required');
    }

    if (!isLegacy) {
      saveItem('signup', '1');
      Amplitude.logEvent(email, 'SIGNUP_LOGIN_CLICK', {}, true);
    }
    Tracking.handleSignInClick('button');
  };

  if (isRequestAccessWrongAccountRedirect) {
    return (
      <div css={styles.container}>
        <div css={styles.signInWrapper} data-testid="sign-in-form-wrapper">
          <div css={styles.formWrapper}>
            <SignInRequestAccessRedirectForm
              formData={formData}
              onPasswordInputChange={handlePasswordInputChange}
              onSubmit={handleSubmit}
              onSignInClick={handleSignInClick}
              formState={formState}
              validationError={passwordValidationError}
            />
          </div>
          <SignInLoginMethods formData={formData} setSigninMethod={setSigninMethod} />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sign-in-form-div-wrap" css={styles.container}>
      {!isLegacy && (
        <Heading size="sm" css={styles.headerEmailRecognized}>
          We recognized your email address
        </Heading>
      )}
      {Location.getUrlParam(`client_id`) === import.meta.env.VITE_APP_CONTEXT_EVENT_CLIENT_ID ? (
        <>
          <Heading size="lg" bold>
            <b>Log in to save your</b> con<b>text| spot</b>
          </Heading>
          <Text css={styles.contextEventSubHeading}>
            Use your LiveChat, ChatBot, HelpDesk, or OpenWidget account to get access to new
            features.
          </Text>
        </>
      ) : (
        <Heading size="lg">Log in</Heading>
      )}
      <div css={styles.signInWrapper} data-testid="sign-in-form-wrapper">
        <div css={styles.formWrapper}>
          <Form onSubmit={handleSubmit}>
            <SigninException />

            <FormField
              labelText="Business email"
              labelFor="email"
              css={styles.formField}
              error={emailValidationError}
            >
              <Input
                autoFocus
                type="email"
                name="email"
                placeholder="name@work-email.com"
                value={email}
                inputSize="large"
                disabled={!isLegacy}
                css={styles.disabledField(!isLegacy)}
                onChange={handleEmailInputChange}
                error={!!emailValidationError}
              />
            </FormField>
            <FormField
              labelText="Password"
              labelFor="password"
              css={styles.formField}
              error={passwordValidationError}
              labelRightNode={
                <CustomLink tabIndex={-1} to="/forgot-password">
                  Forgot password?
                </CustomLink>
              }
            >
              <Input
                type="password"
                name="password"
                placeholder="12 characters or more"
                id="sign-in-password-field"
                inputSize="large"
                onChange={handlePasswordInputChange}
                error={!!passwordValidationError}
              />
            </FormField>

            <Button
              type="submit"
              kind="primary"
              size="large"
              onClick={handleSignInClick}
              loading={formState === FormStates.Requesting}
              data-testid="login-form-button"
              css={styles.logInButton}
            >
              Log in
            </Button>
          </Form>
        </div>

        <SignInLoginMethods formData={formData} setSigninMethod={setSigninMethod} />
      </div>
    </div>
  );
};
